import type { Project } from "./models.js";

// Project identity colors draw from the emerald chart ramp (DESIGN.md): the
// stored value is a token reference ("chart-1"…"chart-5") so the palette can
// be retuned in globals.css without touching saved data. Raw OKLCH strings are
// tolerated as an extension point but never produced by the app itself.
export const PROJECT_COLOR_TOKENS = [
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
] as const;

export type ProjectColorToken = (typeof PROJECT_COLOR_TOKENS)[number];

export function isProjectColorToken(value: string): value is ProjectColorToken {
  return (PROJECT_COLOR_TOKENS as readonly string[]).includes(value);
}

/** Color assigned to the nth project (cycles through the ramp). */
export function projectColorForIndex(index: number): ProjectColorToken {
  const safe = Number.isFinite(index) ? Math.abs(Math.trunc(index)) : 0;
  return PROJECT_COLOR_TOKENS[safe % PROJECT_COLOR_TOKENS.length]!;
}

// Stable, cheap string hash (djb2). Used only to give color-less legacy
// projects a deterministic swatch, so the same project always renders the
// same color without a storage migration.
function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return hash >>> 0;
}

/**
 * Resolve a project's color to either a ramp token or a raw CSS color value.
 * Projects saved before colors existed fall back to a stable id-derived token.
 */
export function resolveProjectColor(project: Pick<Project, "id" | "color">): string {
  const color = project.color?.trim();
  if (color) {
    return color;
  }
  return projectColorForIndex(hashString(project.id));
}

/** CSS value for a stored project color: `var(--chart-n)` for tokens, raw otherwise. */
export function projectColorCssValue(color: string): string {
  return isProjectColorToken(color) ? `var(--${color})` : color;
}

/** Convenience: resolve + toCss in one step. */
export function projectColorCss(project: Pick<Project, "id" | "color">): string {
  return projectColorCssValue(resolveProjectColor(project));
}
